import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronRight, LogOut, LayoutDashboard } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  {
    label: "Directions",
    children: [
      { to: "/directions", label: "Toutes les Directions" },
      { to: "/directions/bibliotheques-musees", label: "Recherche, Bibliothèques et Musées" },
      { to: "/directions/bibliotheques-musees/cecom", label: "· CECOM" },
      { to: "/directions/bibliotheques-musees/pusic", label: "· PUSIC" },
      { to: "/directions/bibliotheques-musees/formation", label: "· Formation" },
      { to: "/directions/bibliotheques-musees/bibliotheques-musees", label: "· Bibliothèques et Musées" },
      { to: "/directions/ecole-doctorale", label: "École Doctorale" },
      { to: "/directions/suivi-encadrement", label: "Suivi et Encadrement" },
    ],
  },
  {
    label: "Plateformes",
    children: [
      { to: "/plateformes/depots", label: "Dépôts des travaux académiques" },
      { to: "/plateformes/archives-academiques", label: "Dépôt institutionnel" },
      { to: "/plateformes/revues", label: "Les Cahiers Congolais de Communication" },
    ],
  },
  { to: "/actualites", label: "Actualités" },
  { to: "/suivi-depot", label: "Suivi de dépôt" },
];

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200/70"
      data-testid="public-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <button
                  className={`nav-item ${openMenu === item.label ? "active" : ""}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </button>
                {openMenu === item.label && (
                  <div className="absolute left-0 top-full pt-2 w-72">
                    <div className="bg-white border border-slate-200 shadow-lg rounded-sm py-2">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          className="flex items-center justify-between px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-unisic-blue"
                          data-testid={`nav-item-${c.to.replaceAll("/", "-")}`}
                        >
                          <span>{c.label}</span>
                          <ChevronRight className="w-3.5 h-3.5 opacity-40" />
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/dashboard"
                className="btn-secondary text-sm py-2"
                data-testid="header-dashboard"
              >
                <LayoutDashboard className="w-4 h-4" /> Tableau de bord
              </Link>
              <button
                onClick={logout}
                className="text-sm text-slate-600 hover:text-unisic-red flex items-center gap-1"
                data-testid="header-logout"
              >
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </>
          ) : (
            <Link to="/connexion" className="btn-primary text-sm py-2" data-testid="header-login">
              Connexion
            </Link>
          )}
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
          data-testid="header-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
          <div className="px-4 py-4 space-y-3">
            {NAV.map((item) =>
              item.children ? (
                <details key={item.label} className="group">
                  <summary className="py-2 text-sm font-semibold text-unisic-blue cursor-pointer">
                    {item.label}
                  </summary>
                  <div className="pl-3 border-l-2 border-unisic-gold/40 ml-1 mt-1 space-y-1">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        className="block py-1.5 text-sm text-slate-700"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </details>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="block py-2 text-sm font-medium text-slate-700"
                >
                  {item.label}
                </Link>
              )
            )}
            <div className="pt-3 border-t border-slate-200">
              {user ? (
                <div className="flex flex-col gap-2">
                  <Link to="/dashboard" className="btn-secondary text-sm justify-center" onClick={() => setOpen(false)}>
                    Tableau de bord
                  </Link>
                  <button onClick={logout} className="btn-secondary text-sm justify-center">
                    Déconnexion
                  </button>
                </div>
              ) : (
                <Link to="/connexion" className="btn-primary text-sm w-full justify-center" onClick={() => setOpen(false)}>
                  Connexion
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Force close menu on route change */}
      <span aria-hidden data-loc={location.pathname} className="hidden" />
    </header>
  );
}
